import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { ActivityCreateInput, OrganizationListQuery } from '@takumi/shared';
import { toast } from 'sonner';
import { api, errorMessage } from './api';
import type { Activity, MyDay, NavCounts, OrgDetail, OrgSummary, Paged, Stage, Task, UserOption } from './types';

export const keys = {
  orgs: (q?: unknown) => ['orgs', q] as const,
  org: (id: string) => ['org', id] as const,
  timeline: (id: string) => ['timeline', id] as const,
  stages: ['stages'] as const,
  board: ['board'] as const,
  myDay: (scope: string) => ['myDay', scope] as const,
  counts: ['counts'] as const,
  tasks: (q?: unknown) => ['tasks', q] as const,
  users: ['userOptions'] as const,
};

/** Everything that shows CRM state; invalidated after any CRM mutation. */
export function useInvalidateCrm() {
  const qc = useQueryClient();
  return (orgId?: string | null) => {
    void qc.invalidateQueries({ queryKey: ['orgs'] });
    void qc.invalidateQueries({ queryKey: ['board'] });
    void qc.invalidateQueries({ queryKey: ['myDay'] });
    void qc.invalidateQueries({ queryKey: ['counts'] });
    void qc.invalidateQueries({ queryKey: ['tasks'] });
    void qc.invalidateQueries({ queryKey: ['notifications'] });
    void qc.invalidateQueries({ queryKey: ['salesMyDay'] });
    void qc.invalidateQueries({ queryKey: ['products'] });
    void qc.invalidateQueries({ queryKey: ['quotes'] });
    void qc.invalidateQueries({ queryKey: ['orders'] });
    if (orgId) {
      void qc.invalidateQueries({ queryKey: ['orgSales', orgId] });
      void qc.invalidateQueries({ queryKey: keys.org(orgId) });
      void qc.invalidateQueries({ queryKey: keys.timeline(orgId) });
    }
  };
}

export function useOrgs(q: Partial<OrganizationListQuery>) {
  return useQuery({
    queryKey: keys.orgs(q),
    queryFn: () => api.get<Paged<OrgSummary>>('/organizations', q as Record<string, string>),
    placeholderData: keepPreviousData,
  });
}

export function useOrg(id: string) {
  return useQuery({ queryKey: keys.org(id), queryFn: () => api.get<OrgDetail>(`/organizations/${id}`), enabled: !!id });
}

export function useTimeline(id: string) {
  return useQuery({ queryKey: keys.timeline(id), queryFn: () => api.get<Activity[]>(`/organizations/${id}/timeline`, { limit: 200 }) });
}

export function useStages() {
  return useQuery({ queryKey: keys.stages, queryFn: () => api.get<Stage[]>('/pipeline/stages'), staleTime: 5 * 60_000 });
}

export function useUserOptions() {
  return useQuery({ queryKey: keys.users, queryFn: () => api.get<UserOption[]>('/users/options'), staleTime: 5 * 60_000 });
}

export function useMyDay(scope: 'me' | 'all') {
  return useQuery({ queryKey: keys.myDay(scope), queryFn: () => api.get<MyDay>('/my-day', { scope }), refetchInterval: 60_000 });
}

export function useCounts(enabled = true) {
  return useQuery({ queryKey: keys.counts, queryFn: () => api.get<NavCounts>('/my-day/counts'), refetchInterval: 45_000, enabled });
}

export function useTasks(q: Record<string, string | undefined>) {
  return useQuery({ queryKey: keys.tasks(q), queryFn: () => api.get<Task[]>('/tasks', q), placeholderData: keepPreviousData });
}

export function useLogActivity(orgId: string) {
  const invalidate = useInvalidateCrm();
  return useMutation({
    mutationFn: (body: Partial<ActivityCreateInput>) => api.post<{ movedStage: boolean }>(`/organizations/${orgId}/activities`, body),
    onSuccess: () => invalidate(orgId),
    onError: (e) => toast.error(errorMessage(e)),
  });
}

export function useCompleteTask() {
  const invalidate = useInvalidateCrm();
  return useMutation({
    mutationFn: (v: { id: string; organizationId?: string | null; outcome?: string | null; next?: { dueAt: string; title?: string } | null }) =>
      api.post<{ task: Task; next: Task | null }>(`/tasks/${v.id}/complete`, { outcome: v.outcome ?? null, next: v.next ?? null }),
    onSuccess: (_d, v) => invalidate(v.organizationId),
    onError: (e) => toast.error(errorMessage(e)),
  });
}

export function useMoveStage() {
  const invalidate = useInvalidateCrm();
  return useMutation({
    mutationFn: (v: { orgId: string; stageId: string; lostReason?: string | null }) =>
      api.post<{ converted: boolean }>(`/organizations/${v.orgId}/stage`, { stageId: v.stageId, lostReason: v.lostReason ?? null }),
    onSuccess: (d, v) => {
      invalidate(v.orgId);
      if (d.converted) toast.success('הליד הפך ללקוח 🎉');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
}
